import { Fragment, createElement } from 'react';

import type { ReactNode } from 'react';

import DOMPurify from 'dompurify';

const SUPPORTED_TAGS = new Set([
  'a',
  'b',
  'br',
  'em',
  'i',
  'li',
  'ol',
  'p',
  'span',
  'strong',
  'u',
  'ul'
]);

const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

const getHref = (element: Element): string | undefined => {
  const href = element.getAttribute('href');
  if (!href) return undefined;

  try {
    const parsedUrl = new URL(href, 'https://app.gx.example');
    if (!SAFE_PROTOCOLS.has(parsedUrl.protocol)) return undefined;
    return href;
  } catch {
    return undefined;
  }
};

const convertNode = (node: ChildNode, key: string): ReactNode => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as Element;
  const tagName = element.tagName.toLowerCase();
  const children = Array.from(element.childNodes).map((child, index) =>
    convertNode(child, `${key}-${index}`)
  );

  if (!SUPPORTED_TAGS.has(tagName)) {
    return <Fragment key={key}>{children}</Fragment>;
  }

  if (tagName === 'a') {
    return createElement(
      'a',
      {
        key,
        href: getHref(element),
        rel: 'noopener noreferrer',
        target: '_blank'
      },
      ...children
    );
  }

  return createElement(tagName, { key }, ...children);
};

const renderSanitizedHtml = (html: string): ReactNode => {
  const sanitizedHtml = DOMPurify.sanitize(html);
  const parsedDocument = new DOMParser().parseFromString(sanitizedHtml, 'text/html');

  return parsedDocument.body.childNodes.length > 0
    ? Array.from(parsedDocument.body.childNodes).map((node, index) =>
        convertNode(node, `node-${index}`)
      )
    : null;
};

export default renderSanitizedHtml;
